import React, { useCallback, useMemo } from 'react'
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { Select } from '~components/common'
import { Icon } from '~components/internal'

// =============================================================================
// TYPES
// =============================================================================

export type JsonSchemaPropertyType = 'string' | 'number' | 'boolean' | 'object' | 'array'
export type ArrayItemType = 'string' | 'number' | 'boolean'

export interface JsonSchemaProperty {
  type: JsonSchemaPropertyType
  description?: string
  properties?: Record<string, JsonSchemaProperty>
  items?: { type: ArrayItemType }
}

// =============================================================================
// JSON <-> SCHEMA CONVERSION UTILITIES
// =============================================================================

/**
 * Infers a JsonSchemaProperty from a JSON value.
 * Useful for converting raw JSON into a schema structure.
 */
export const inferSchemaFromJson = (
  value: unknown,
  maxDepth: number = 5,
  currentDepth: number = 0,
): JsonSchemaProperty => {
  if (currentDepth >= maxDepth) {
    return { type: 'object' }
  }

  if (value === null || value === undefined) {
    return { type: 'string' }
  }

  if (typeof value === 'string') {
    return { type: 'string' }
  }

  if (typeof value === 'number') {
    return { type: 'number' }
  }

  if (typeof value === 'boolean') {
    return { type: 'boolean' }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return { type: 'array', items: { type: 'string' } }
    }

    const firstItem = value[0]
    if (typeof firstItem === 'string') {
      return { type: 'array', items: { type: 'string' } }
    } else if (typeof firstItem === 'number') {
      return { type: 'array', items: { type: 'number' } }
    } else if (typeof firstItem === 'boolean') {
      return { type: 'array', items: { type: 'boolean' } }
    }
    return { type: 'array', items: { type: 'string' } }
  }

  if (typeof value === 'object') {
    const properties: Record<string, JsonSchemaProperty> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      properties[key] = inferSchemaFromJson(val, maxDepth, currentDepth + 1)
    }
    return { type: 'object', properties }
  }

  return { type: 'string' }
}

/**
 * Parses a JSON string and converts it to a schema.
 * Returns null if the JSON is invalid.
 */
export const parseJsonToSchema = (
  jsonString: string,
  maxDepth: number = 5,
): JsonSchemaProperty | null => {
  try {
    const parsed = JSON.parse(jsonString)
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { type: 'object', properties: { root: inferSchemaFromJson(parsed, maxDepth) } }
    }
    return inferSchemaFromJson(parsed, maxDepth)
  } catch {
    return null
  }
}

/**
 * Generates a sample JSON object from a schema with type placeholders.
 * Uses type names as values (e.g., "<string>") for primitive types.
 */
export const schemaToSampleJson = (schema: JsonSchemaProperty): unknown => {
  switch (schema.type) {
    case 'string':
      return '<string>'
    case 'number':
      return '<number>'
    case 'boolean':
      return '<boolean>'
    case 'array':
      if (schema.items) {
        return [`<${schema.items.type}>`]
      }
      return ['<string>']
    case 'object':
      if (!schema.properties || Object.keys(schema.properties).length === 0) {
        return {}
      }
      const result: Record<string, unknown> = {}
      for (const [key, prop] of Object.entries(schema.properties)) {
        result[key] = schemaToSampleJson(prop)
      }
      return result
    default:
      return null
  }
}

/**
 * Converts a schema to a formatted JSON string (for display in raw mode).
 * Preserves descriptions as comments in the output.
 */
export const schemaToJsonString = (schema: JsonSchemaProperty): string => {
  return JSON.stringify(schemaToSampleJson(schema), null, 2)
}

/**
 * Parses type placeholder strings back to schema types.
 */
const parseTypePlaceholder = (value: string): JsonSchemaPropertyType | null => {
  const match = value.match(/^<(string|number|boolean)>$/)
  if (match) {
    return match[1] as JsonSchemaPropertyType
  }
  return null
}

/**
 * Infers schema from JSON that may contain type placeholders.
 * Preserves existing descriptions from the original schema.
 */
export const inferSchemaFromJsonWithDescriptions = (
  value: unknown,
  originalSchema?: JsonSchemaProperty,
  maxDepth: number = 5,
  currentDepth: number = 0,
): JsonSchemaProperty => {
  if (currentDepth >= maxDepth) {
    return { type: 'object' }
  }

  if (value === null || value === undefined) {
    return { type: 'string', description: originalSchema?.description }
  }

  if (typeof value === 'string') {
    const placeholderType = parseTypePlaceholder(value)
    if (placeholderType) {
      return { type: placeholderType, description: originalSchema?.description }
    }
    return { type: 'string', description: originalSchema?.description }
  }

  if (typeof value === 'number') {
    return { type: 'number', description: originalSchema?.description }
  }

  if (typeof value === 'boolean') {
    return { type: 'boolean', description: originalSchema?.description }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return {
        type: 'array',
        items: { type: 'string' },
        description: originalSchema?.description,
      }
    }

    const firstItem = value[0]
    if (typeof firstItem === 'string') {
      const placeholderType = parseTypePlaceholder(firstItem)
      if (placeholderType && ['string', 'number', 'boolean'].includes(placeholderType)) {
        return {
          type: 'array',
          items: { type: placeholderType as ArrayItemType },
          description: originalSchema?.description,
        }
      }
      return {
        type: 'array',
        items: { type: 'string' },
        description: originalSchema?.description,
      }
    } else if (typeof firstItem === 'number') {
      return {
        type: 'array',
        items: { type: 'number' },
        description: originalSchema?.description,
      }
    } else if (typeof firstItem === 'boolean') {
      return {
        type: 'array',
        items: { type: 'boolean' },
        description: originalSchema?.description,
      }
    }
    return {
      type: 'array',
      items: { type: 'string' },
      description: originalSchema?.description,
    }
  }

  if (typeof value === 'object') {
    const properties: Record<string, JsonSchemaProperty> = {}
    const originalProps = originalSchema?.properties || {}

    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      properties[key] = inferSchemaFromJsonWithDescriptions(
        val,
        originalProps[key],
        maxDepth,
        currentDepth + 1,
      )
    }
    return { type: 'object', properties, description: originalSchema?.description }
  }

  return { type: 'string', description: originalSchema?.description }
}

interface PropertyNode {
  id: string
  name: string
  type: JsonSchemaPropertyType
  description: string
  required: boolean
  expanded: boolean
  arrayItemType?: ArrayItemType
  children: PropertyNode[]
}

export interface JsonSchemaBuilderProps {
  schema: JsonSchemaProperty
  requiredPaths: string[]
  onChange: (schema: JsonSchemaProperty, requiredPaths: string[]) => void
  maxDepth?: number
}

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_DEPTH_DEFAULT = 5

const PROPERTY_TYPES: { value: JsonSchemaPropertyType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
]

const ARRAY_ITEM_TYPES: { value: ArrayItemType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
]

// =============================================================================
// UTILITIES
// =============================================================================

let nodeIdCounter = 0
const generateNodeId = (): string => `node_${++nodeIdCounter}_${Date.now()}`

const schemaToNodes = (
  schema: JsonSchemaProperty,
  requiredPaths: string[],
  parentPath: string = '',
): PropertyNode[] => {
  if (schema.type !== 'object' || !schema.properties) {
    return []
  }

  return Object.entries(schema.properties).map(([name, prop]) => {
    const currentPath = parentPath ? `${parentPath}.${name}` : name
    const isRequired = requiredPaths.includes(currentPath)

    const node: PropertyNode = {
      id: generateNodeId(),
      name,
      type: prop.type,
      description: prop.description || '',
      required: isRequired,
      expanded: true,
      arrayItemType: prop.items?.type,
      children: prop.type === 'object' ? schemaToNodes(prop, requiredPaths, currentPath) : [],
    }

    return node
  })
}

const nodesToSchema = (nodes: PropertyNode[]): JsonSchemaProperty => {
  const properties: Record<string, JsonSchemaProperty> = {}

  nodes.forEach((node) => {
    const prop: JsonSchemaProperty = {
      type: node.type,
    }

    if (node.description) {
      prop.description = node.description
    }

    if (node.type === 'object' && node.children.length > 0) {
      prop.properties = nodesToSchema(node.children).properties
    }

    if (node.type === 'array' && node.arrayItemType) {
      prop.items = { type: node.arrayItemType }
    }

    properties[node.name] = prop
  })

  return {
    type: 'object',
    properties,
  }
}

const collectRequiredPaths = (nodes: PropertyNode[], parentPath: string = ''): string[] => {
  const paths: string[] = []

  nodes.forEach((node) => {
    const currentPath = parentPath ? `${parentPath}.${node.name}` : node.name

    if (node.required && node.name) {
      paths.push(currentPath)
    }

    if (node.type === 'object' && node.children.length > 0) {
      paths.push(...collectRequiredPaths(node.children, currentPath))
    }
  })

  return paths
}

const createEmptyNode = (): PropertyNode => ({
  id: generateNodeId(),
  name: '',
  type: 'string',
  description: '',
  required: false,
  expanded: true,
  children: [],
})

// =============================================================================
// PROPERTY ROW COMPONENT
// =============================================================================

interface PropertyRowProps {
  node: PropertyNode
  depth: number
  maxDepth: number
  onUpdate: (id: string, updates: Partial<PropertyNode>) => void
  onDelete: (id: string) => void
  onAddChild: (parentId: string) => void
}

const PropertyRow: React.FC<PropertyRowProps> = ({
  node,
  depth,
  maxDepth,
  onUpdate,
  onDelete,
  onAddChild,
}) => {
  const canAddChildren = node.type === 'object' && depth < maxDepth
  const hasChildren = node.children.length > 0

  const handleToggleExpand = (): void => {
    onUpdate(node.id, { expanded: !node.expanded })
  }

  const handleTypeChange = (newType: JsonSchemaPropertyType): void => {
    const updates: Partial<PropertyNode> = { type: newType }

    if (newType === 'object') {
      updates.children = []
      updates.arrayItemType = undefined
    } else if (newType === 'array') {
      updates.arrayItemType = 'string'
      updates.children = []
    } else {
      updates.children = []
      updates.arrayItemType = undefined
    }

    onUpdate(node.id, updates)
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          paddingLeft: `${12 + depth * 24}px`,
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          '&:hover': { backgroundColor: 'background.secondary' },
        }}
      >
        {/* Expand/Collapse for objects */}
        <Box sx={{ width: '28px', flexShrink: 0 }}>
          {node.type === 'object' && (
            <Tooltip
              title={node.expanded ? 'Collapse nested properties' : 'Expand nested properties'}
            >
              <IconButton size='small' onClick={handleToggleExpand} sx={{ padding: '2px' }}>
                {node.expanded ? (
                  <Icon name='ExpandLess' size={18} />
                ) : (
                  <Icon name='ExpandMore' size={18} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Name */}
        <TextField
          value={node.name}
          onChange={(e) => onUpdate(node.id, { name: e.target.value })}
          placeholder='Property name'
          size='small'
          variant='standard'
          sx={{
            flex: 1,
            minWidth: '120px',
            '& input': { padding: '4px 0', fontSize: '13px' },
          }}
          slotProps={{
            input: { disableUnderline: true },
          }}
        />

        {/* Type */}
        <Select
          value={node.type}
          onChange={(e) => handleTypeChange(e.target.value as JsonSchemaPropertyType)}
          size='small'
          variant='outlined'
          sx={{
            width: '100px',
            fontSize: '12px',
            '& .MuiOutlinedInput-notchedOutline': { borderRadius: '5px' },
            '& .MuiSelect-select': { padding: '6px 10px' },
          }}
        >
          {PROPERTY_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value} sx={{ fontSize: '12px' }}>
              {type.label}
            </MenuItem>
          ))}
        </Select>

        {/* Array Item Type */}
        {node.type === 'array' && (
          <Select
            value={node.arrayItemType || 'string'}
            onChange={(e) => onUpdate(node.id, { arrayItemType: e.target.value as ArrayItemType })}
            size='small'
            variant='outlined'
            sx={{
              width: '100px',
              fontSize: '12px',
              '& .MuiOutlinedInput-notchedOutline': { borderRadius: '5px' },
              '& .MuiSelect-select': { padding: '6px 10px' },
            }}
          >
            {ARRAY_ITEM_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value} sx={{ fontSize: '12px' }}>
                {type.label}[]
              </MenuItem>
            ))}
          </Select>
        )}

        {/* Description */}
        <TextField
          value={node.description}
          onChange={(e) => onUpdate(node.id, { description: e.target.value })}
          placeholder='Description'
          size='small'
          variant='standard'
          sx={{
            flex: 1.5,
            '& input': { padding: '4px 0', fontSize: '13px' },
          }}
          slotProps={{
            input: { disableUnderline: true },
          }}
        />

        {/* Required checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={node.required}
              onChange={(e) => onUpdate(node.id, { required: e.target.checked })}
              size='small'
              sx={{ padding: '4px' }}
            />
          }
          label={
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Required</Typography>
          }
          sx={{ margin: 0, marginRight: '8px' }}
        />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: '4px' }}>
          {canAddChildren && (
            <Tooltip title='Add nested property'>
              <IconButton
                size='small'
                onClick={() => onAddChild(node.id)}
                sx={{ color: 'text.secondary' }}
              >
                <Icon name='Add' size={18} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Delete property'>
            <IconButton
              size='small'
              onClick={() => onDelete(node.id)}
              sx={{ color: 'text.disabled' }}
            >
              <Icon name='DeleteOutlined' size={18} fill={0} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Nested children */}
      {node.type === 'object' && hasChildren && (
        <Collapse in={node.expanded}>
          {node.children.map((child) => (
            <PropertyRow
              key={child.id}
              node={child}
              depth={depth + 1}
              maxDepth={maxDepth}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </Collapse>
      )}
    </Box>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const JsonSchemaBuilder: React.FC<JsonSchemaBuilderProps> = ({
  schema,
  requiredPaths,
  onChange,
  maxDepth = MAX_DEPTH_DEFAULT,
}) => {
  const [nodes, setNodes] = React.useState<PropertyNode[]>(() =>
    schemaToNodes(schema, requiredPaths),
  )

  const updateNodesAndNotify = useCallback(
    (newNodes: PropertyNode[]): void => {
      setNodes(newNodes)
      const newSchema = nodesToSchema(newNodes)
      const newRequiredPaths = collectRequiredPaths(newNodes)
      onChange(newSchema, newRequiredPaths)
    },
    [onChange],
  )

  const findAndUpdateNode = useCallback(
    (
      nodes: PropertyNode[],
      targetId: string,
      updates: Partial<PropertyNode>,
    ): PropertyNode[] | null => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === targetId) {
          const newNodes = [...nodes]
          newNodes[i] = { ...newNodes[i], ...updates }
          return newNodes
        }

        if (nodes[i].children.length > 0) {
          const updatedChildren = findAndUpdateNode(nodes[i].children, targetId, updates)
          if (updatedChildren) {
            const newNodes = [...nodes]
            newNodes[i] = { ...newNodes[i], children: updatedChildren }
            return newNodes
          }
        }
      }
      return null
    },
    [],
  )

  const findAndDeleteNode = useCallback(
    (nodes: PropertyNode[], targetId: string): PropertyNode[] | null => {
      const index = nodes.findIndex((n) => n.id === targetId)
      if (index !== -1) {
        return nodes.filter((n) => n.id !== targetId)
      }

      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].children.length > 0) {
          const updatedChildren = findAndDeleteNode(nodes[i].children, targetId)
          if (updatedChildren) {
            const newNodes = [...nodes]
            newNodes[i] = { ...newNodes[i], children: updatedChildren }
            return newNodes
          }
        }
      }
      return null
    },
    [],
  )

  const findAndAddChild = useCallback(
    (nodes: PropertyNode[], parentId: string): PropertyNode[] | null => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === parentId) {
          const newNodes = [...nodes]
          newNodes[i] = {
            ...newNodes[i],
            expanded: true,
            children: [...newNodes[i].children, createEmptyNode()],
          }
          return newNodes
        }

        if (nodes[i].children.length > 0) {
          const updatedChildren = findAndAddChild(nodes[i].children, parentId)
          if (updatedChildren) {
            const newNodes = [...nodes]
            newNodes[i] = { ...newNodes[i], children: updatedChildren }
            return newNodes
          }
        }
      }
      return null
    },
    [],
  )

  const handleUpdateNode = useCallback(
    (id: string, updates: Partial<PropertyNode>): void => {
      const updated = findAndUpdateNode(nodes, id, updates)
      if (updated) {
        updateNodesAndNotify(updated)
      }
    },
    [nodes, findAndUpdateNode, updateNodesAndNotify],
  )

  const handleDeleteNode = useCallback(
    (id: string): void => {
      const updated = findAndDeleteNode(nodes, id)
      if (updated) {
        updateNodesAndNotify(updated)
      }
    },
    [nodes, findAndDeleteNode, updateNodesAndNotify],
  )

  const handleAddChild = useCallback(
    (parentId: string): void => {
      const updated = findAndAddChild(nodes, parentId)
      if (updated) {
        updateNodesAndNotify(updated)
      }
    },
    [nodes, findAndAddChild, updateNodesAndNotify],
  )

  const handleAddRootProperty = (): void => {
    updateNodesAndNotify([...nodes, createEmptyNode()])
  }

  const depthIndicator = useMemo(() => {
    const countMaxDepth = (nodes: PropertyNode[], currentDepth: number = 0): number => {
      let max = currentDepth
      nodes.forEach((node) => {
        if (node.type === 'object' && node.children.length > 0) {
          max = Math.max(max, countMaxDepth(node.children, currentDepth + 1))
        }
      })
      return max
    }
    return countMaxDepth(nodes)
  }, [nodes])

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '5px' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          backgroundColor: '#f8f8f8',
          borderBottom: '1px solid #e0e0e0',
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
            Schema Properties
          </Typography>
          {depthIndicator > 0 && (
            <Typography sx={{ fontSize: '11px', color: '#999' }}>
              Depth: {depthIndicator + 1}/{maxDepth}
            </Typography>
          )}
        </Box>
        <Tooltip title={depthIndicator >= maxDepth - 1 ? 'Maximum depth reached' : 'Add property'}>
          <span>
            <IconButton size='small' onClick={handleAddRootProperty} sx={{ color: 'primary.main' }}>
              <Icon name='Add' size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Property List */}
      {nodes.length > 0 ? (
        nodes.map((node) => (
          <PropertyRow
            key={node.id}
            node={node}
            depth={0}
            maxDepth={maxDepth}
            onUpdate={handleUpdateNode}
            onDelete={handleDeleteNode}
            onAddChild={handleAddChild}
          />
        ))
      ) : (
        <Box
          sx={{
            padding: '24px',
            textAlign: 'center',
            color: '#999',
          }}
        >
          <Typography variant='body2'>No properties defined</Typography>
          <Typography variant='caption' sx={{ display: 'block', marginTop: '4px' }}>
            Click the + button to add a property
          </Typography>
        </Box>
      )}

      {/* Required paths preview */}
      {nodes.length > 0 && (
        <Box
          sx={{
            padding: '8px 12px',
            backgroundColor: '#f8f8f8',
            borderTop: '1px solid #e0e0e0',
            borderRadius: '0 0 4px 4px',
          }}
        >
          <Typography sx={{ fontSize: '11px', color: '#666' }}>
            Required paths:{' '}
            {collectRequiredPaths(nodes).join(', ') || <em style={{ color: '#999' }}>none</em>}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default JsonSchemaBuilder
